import { useMemo, useState } from "react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Show,
  Text,
  useToken,
} from "@chakra-ui/react";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import type { APIListingPerformanceStat } from "models/types/api-listing";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import BarChartIcon from "assets/icons/medium/bar-chart.svg?react";
import { SimpleGridTable } from "components/simple-grid-table";
import { SkeletonGrid } from "components/skeleton-grid";
import { StatsCard } from "components/stats-card";
import { StatsCardSkeleton } from "components/stats-card-skeleton";
import { AD_PLATFORMS } from "modules/vendor-portal/screens/performance/utils/ad-platform-details";

export const FILTER_BUTTON_OPTIONS: { id: PeriodKey; label: string }[] = [
  { id: "total", label: "Total campaign" },
  { id: "last7days", label: "Last 7 days" },
  { id: "last30days", label: "Last 30 days" },
];

type PeriodKey = "total" | "last7days" | "last30days";

/**
 * Takes an object where each key is a day (e.g. "2024-01-01") and then
 * returns a keyed set of stats for that period, factoring in the aggregate
 * method for each stat
 */
function aggregateStatsForPeriod(
  values: APIListingPerformanceStat["value"],
  config: APIListingPerformanceStat["format"]["meta"],
  period: PeriodKey,
) {
  const datesBasedOnPeriod = Object.keys(values).filter((date) =>
    isDateInPeriod(date, period),
  );

  return config.reduce(
    (
      acc: {
        id: string;
        meta: APIListingPerformanceStat["format"]["meta"][number];
        value: number;
      }[],
      stat,
    ) => {
      const valuesForThisStat = datesBasedOnPeriod.map(
        (date) => values[date][stat.key],
      );
      const aggregateMethod = stat.aggregate_method;
      let aggregateValue = valuesForThisStat.reduce((a, b) => a + b, 0);

      if (aggregateMethod === "percentage") {
        aggregateValue = Math.round(aggregateValue / valuesForThisStat.length);
      }

      acc.push({
        id: stat.key,
        meta: stat,
        value: Number.isNaN(aggregateValue) ? 0 : aggregateValue,
      });
      return acc;
    },
    [],
  );
}

function isDateInPeriod(date: string | Dayjs, period: PeriodKey) {
  if (period === "total") return true;
  if (period === "last7days")
    return dayjs(date).isAfter(dayjs().subtract(7, "day"));
  if (period === "last30days")
    return dayjs(date).isAfter(dayjs().subtract(30, "day"));
  return false;
}

type PerformanceAdCampaignProps = {
  adCampaign: APIListingPerformanceStat;
  isLoading: boolean;
};

export function PerformanceAdCampaign({
  adCampaign,
  isLoading,
}: PerformanceAdCampaignProps) {
  const platformConfig = AD_PLATFORMS[adCampaign.format.id];
  const [selectedCampaignPeriod, setSelectedCampaignPeriod] =
    useState<PeriodKey>(FILTER_BUTTON_OPTIONS[0].id);

  const stats = useMemo(
    () =>
      aggregateStatsForPeriod(
        adCampaign.value,
        adCampaign.format.meta,
        selectedCampaignPeriod,
      ),
    [adCampaign.value, adCampaign.format.meta, selectedCampaignPeriod],
  );

  const primaryStats = useMemo(
    () => stats.filter((stat) => stat.meta.importance === "primary"),
    [stats],
  );

  const secondaryStats = useMemo(
    () =>
      stats.filter(
        (stat) =>
          stat.meta.importance === "secondary" &&
          adCampaign.format.id !== "reach" &&
          stat.value > 0,
      ),
    [adCampaign.format.id, stats],
  );

  const secondaryStatsSections = useMemo(
    () => [...new Set(secondaryStats.map((stat) => stat.meta.section))],
    [secondaryStats],
  );

  const lineChartStats = useMemo(
    () =>
      adCampaign.type === "summary"
        ? null
        : Object.keys(adCampaign.value)
            .map((date) => {
              return {
                clicks: platformConfig.clicks_key
                  ? adCampaign.value[date][platformConfig.clicks_key]
                  : null,
                impressions: platformConfig.impressions_key
                  ? adCampaign.value[date][platformConfig.impressions_key]
                  : null,
                date: date,
              };
            })
            .filter((stat) =>
              isDateInPeriod(stat.date, selectedCampaignPeriod),
            ),
    [
      adCampaign.type,
      adCampaign.value,
      platformConfig.clicks_key,
      platformConfig.impressions_key,
      selectedCampaignPeriod,
    ],
  );

  const chartLinePink = useToken("colors", "chartLinePink");
  const chartLineBlue = useToken("colors", "chartLineBlue");

  return (
    <Flex direction={"column"} gap={10}>
      <Flex justify={"space-between"} wrap={"wrap"} gap={4} minH={"32px"}>
        <HStack gap={4}>
          <Text fontSize={"xl"} fontWeight={600} color={"gray.700"}>
            Campaign snapshot
          </Text>
        </HStack>
        {adCampaign.type !== "summary" && (
          <Show above={"md"}>
            <ButtonGroup size={"sm"} isAttached variant={"group"}>
              {FILTER_BUTTON_OPTIONS.map((option) => (
                <Button
                  key={option.id}
                  isActive={option.id === selectedCampaignPeriod}
                  onClick={() => setSelectedCampaignPeriod(option.id)}
                >
                  {option.label}
                </Button>
              ))}
            </ButtonGroup>
          </Show>
        )}
      </Flex>

      {adCampaign.type !== "summary" && (
        <Show below={"md"}>
          <Menu matchWidth={true}>
            <MenuButton
              as={Button}
              variant={"outline"}
              colorScheme={"gray"}
              rightIcon={<ChevronDownIcon />}
            >
              {
                FILTER_BUTTON_OPTIONS.find(
                  (option) => option.id === selectedCampaignPeriod,
                )?.label
              }
            </MenuButton>
            <MenuList>
              {FILTER_BUTTON_OPTIONS.map((option) => (
                <MenuItem
                  key={option.id}
                  onClick={() => setSelectedCampaignPeriod(option.id)}
                >
                  {option.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Show>
      )}

      <SkeletonGrid
        isLoaded={!isLoading}
        gridSize={4}
        skeleton={<StatsCardSkeleton />}
      >
        <Grid
          templateColumns={{
            base: "repeat(2, 1fr)",
            xl: "repeat(4, 1fr)",
          }}
          templateRows={{
            base: "repeat(2, 1fr)",
            xl: "repeat(1, 1fr)",
          }}
          gap={6}
        >
          {primaryStats.map((stat) => (
            <GridItem key={stat.id} w={"100%"}>
              <StatsCard
                stat={{
                  id: stat.id,
                  label: stat.meta.label,
                  value: `${stat.value}${
                    stat.meta.aggregate_method === "percentage" ? "%" : ""
                  }`,
                }}
                icon={BarChartIcon}
              />
            </GridItem>
          ))}
        </Grid>
      </SkeletonGrid>

      {adCampaign.format.id !== "reach" && !!lineChartStats ? (
        <Flex direction={"column"} gap={6}>
          <Heading fontSize={"xl"} fontWeight={600}>
            Engagement
          </Heading>
          <Center h={"277px"}>
            <ResponsiveContainer width={"100%"} height={"100%"}>
              <LineChart data={lineChartStats}>
                <XAxis
                  dataKey={"date"}
                  minTickGap={40}
                  tickFormatter={(value) => dayjs(value).format("D MMM")}
                />
                <YAxis />
                <Tooltip
                  animationDuration={50}
                  formatter={(value: string, name: string) => [
                    value,
                    name.charAt(0).toUpperCase() + name.slice(1),
                  ]}
                  labelFormatter={(value) => dayjs(value).format("D MMM")}
                  contentStyle={{
                    borderColor: "var(--chakra-colors-chakra-border-color)",
                    borderRadius: "6px",
                    boxShadow: "0px 3px 10px -2px rgba(0, 0, 20, 0.15)",
                  }}
                />
                <Line
                  dataKey={"clicks"}
                  label={"Clicks"}
                  stroke={chartLinePink}
                />
                <Line
                  dataKey={"impressions"}
                  label={"Impressions"}
                  stroke={chartLineBlue}
                />
              </LineChart>
            </ResponsiveContainer>
          </Center>
          <HStack gap={6}>
            <HStack>
              <Box
                w={"12px"}
                h={"12px"}
                borderRadius={"100%"}
                bg={"chartLineBlue"}
              />
              <Text fontSize={"sm"} fontWeight={500}>
                Impressions (ads shown)
              </Text>
            </HStack>
            <HStack>
              <Box
                w={"12px"}
                h={"12px"}
                borderRadius={"100%"}
                bg={"chartLinePink"}
              />
              <Text fontSize={"sm"} fontWeight={500}>
                Clicks
              </Text>
            </HStack>
          </HStack>
        </Flex>
      ) : null}

      {secondaryStatsSections.map((sectionName) => (
        <Flex direction={"column"} gap={6} key={sectionName}>
          <Heading fontSize={"xl"} fontWeight={600}>
            {sectionName}
          </Heading>
          <SimpleGridTable
            items={secondaryStats
              .filter((stat) => stat.meta.section === sectionName)
              .map((stat) => ({
                label: stat.meta.label,
                value: `${stat.value}${
                  stat.meta.aggregate_method === "percentage" ? "%" : ""
                }`,
              }))}
          />
        </Flex>
      ))}
    </Flex>
  );
}
